import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Ship, 
  MapPin, 
  Clock, 
  Calendar,
  Edit,
  Eye,
  TrendingUp,
  Anchor,
  Navigation,
  Timer,
  CheckCircle,
  AlertCircle,
  Info
} from "lucide-react";
import { format } from "date-fns";

interface Order {
  id: number;
  orderNumber: string;
  product: string;
  quantity: number;
  unit: string;
  status: string;
  
  // Vessel information
  vesselName?: string;
  vesselImoNumber?: string;
  vesselType?: string;
  vesselFlag?: string;
  vesselDwt?: number;
  
  // Port information
  portOfLoading?: string;
  portOfDischarge?: string;
  loadingBerth?: string;
  dischargeBerth?: string;
  
  // Vessel scheduling
  vesselEta?: string;
  vesselEtd?: string;
  vesselAta?: string;
  vesselAtd?: string;
  
  // Current position
  currentLatitude?: number;
  currentLongitude?: number;
  lastPositionUpdate?: string;
  vesselStatus?: string;
  
  // Progress tracking
  progressStage?: string;
  progressPercentage?: number;
  milestones?: any[];
  
  // Cargo details
  cargoDescription?: string;
  loadingDate?: string;
  dischargingDate?: string;
}

interface VesselTrackingProps {
  order: Order;
  onClose?: () => void;
}

// Form schema for vessel information
const vesselTrackingSchema = z.object({
  vesselName: z.string().min(1, "Vessel name is required"),
  vesselImoNumber: z.string().optional(),
  vesselType: z.string().optional(),
  vesselFlag: z.string().optional(),
  vesselDwt: z.string().optional(),
  portOfLoading: z.string().optional(),
  portOfDischarge: z.string().optional(),
  loadingBerth: z.string().optional(),
  dischargeBerth: z.string().optional(),
  vesselEta: z.string().optional(),
  vesselEtd: z.string().optional(),
  vesselStatus: z.string().optional(),
  progressStage: z.string().optional(),
  cargoDescription: z.string().optional(),
  loadingDate: z.string().optional(),
  dischargingDate: z.string().optional(),
});

type VesselTrackingFormData = z.infer<typeof vesselTrackingSchema>;

const VESSEL_TYPES = [
  "bulk_carrier",
  "container_ship", 
  "tanker",
  "general_cargo",
  "roll_on_off",
  "lng_carrier",
  "other"
];

const VESSEL_STATUSES = [
  "unknown",
  "at_sea",
  "at_anchor", 
  "at_berth",
  "loading",
  "discharging",
  "in_transit"
];

const PROGRESS_STAGES = [
  { key: "order_placed", label: "Order Placed", percentage: 10 },
  { key: "vessel_assigned", label: "Vessel Assigned", percentage: 20 },
  { key: "loading", label: "Loading", percentage: 40 },
  { key: "in_transit", label: "In Transit", percentage: 60 },
  { key: "arrived", label: "Arrived at Port", percentage: 80 },
  { key: "discharging", label: "Discharging", percentage: 90 },
  { key: "completed", label: "Completed", percentage: 100 }
];

export default function VesselTracking({ order, onClose }: VesselTrackingProps) {
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showPositionDialog, setShowPositionDialog] = useState(false);

  const { toast } = useToast();

  // Form for editing vessel information
  const vesselForm = useForm<VesselTrackingFormData>({
    resolver: zodResolver(vesselTrackingSchema),
    defaultValues: {
      vesselName: order.vesselName || "",
      vesselImoNumber: order.vesselImoNumber || "",
      vesselType: order.vesselType || "",
      vesselFlag: order.vesselFlag || "",
      vesselDwt: order.vesselDwt?.toString() || "",
      portOfLoading: order.portOfLoading || "",
      portOfDischarge: order.portOfDischarge || "",
      loadingBerth: order.loadingBerth || "",
      dischargeBerth: order.dischargeBerth || "",
      vesselEta: order.vesselEta || "",
      vesselEtd: order.vesselEtd || "",
      vesselStatus: order.vesselStatus || "unknown",
      progressStage: order.progressStage || "order_placed",
      cargoDescription: order.cargoDescription || "",
      loadingDate: order.loadingDate || "",
      dischargingDate: order.dischargingDate || "",
    }
  });

  // Update vessel information mutation
  const updateVesselMutation = useMutation({
    mutationFn: (vesselData: VesselTrackingFormData) => {
      const payload = {
        ...vesselData,
        vesselDwt: vesselData.vesselDwt ? parseFloat(vesselData.vesselDwt) : null,
        progressPercentage: PROGRESS_STAGES.find(s => s.key === vesselData.progressStage)?.percentage || 0
      };
      return apiRequest(`/api/orders/${order.id}/vessel`, 'PUT', payload);
    },
    onSuccess: () => {
      toast({ title: "Success", description: "Vessel information updated successfully" });
      setShowEditDialog(false);
      queryClient.invalidateQueries({ queryKey: ['/api/orders'] });
      queryClient.invalidateQueries({ queryKey: ['/api/orders', order.id] });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update vessel information",
        variant: "destructive"
      });
    }
  });

  // Update vessel position mutation
  const updatePositionMutation = useMutation({
    mutationFn: ({ latitude, longitude }: { latitude: number; longitude: number }) =>
      apiRequest(`/api/orders/${order.id}/vessel/position`, 'PUT', {
        currentLatitude: latitude,
        currentLongitude: longitude,
        lastPositionUpdate: new Date().toISOString()
      }),
    onSuccess: () => {
      toast({ title: "Success", description: "Vessel position updated successfully" });
      setShowPositionDialog(false);
      queryClient.invalidateQueries({ queryKey: ['/api/orders'] });
      queryClient.invalidateQueries({ queryKey: ['/api/orders', order.id] });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update vessel position",
        variant: "destructive"
      });
    }
  });

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'at_sea': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'at_berth': return 'bg-green-100 text-green-800 border-green-200';
      case 'loading': case 'discharging': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'at_anchor': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'in_transit': return 'bg-purple-100 text-purple-800 border-purple-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getProgressStageInfo = (stage: string) => {
    return PROGRESS_STAGES.find(s => s.key === stage) || PROGRESS_STAGES[0];
  };

  const formatDateTime = (dateString: string | undefined) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
    } catch {
      return '-';
    }
  };

  const formatCoordinate = (lat?: number, lng?: number) => {
    if (lat == null || lng == null) return '-';
    return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
  };

  const onUpdateVessel = (data: VesselTrackingFormData) => {
    updateVesselMutation.mutate(data);
  };

  const currentStage = getProgressStageInfo(order.progressStage || 'order_placed');

  return (
    <div className="space-y-6">
      {/* Vessel Overview Card */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Ship className="h-5 w-5" />
                Vessel Tracking - Order #{order.orderNumber}
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                {order.vesselName || 'No vessel assigned'} 
                {order.vesselImoNumber && ` • IMO: ${order.vesselImoNumber}`}
              </p>
            </div>
            <div className="flex gap-2">
              <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
                <DialogTrigger asChild>
                  <Button variant="outline" data-testid="button-edit-vessel">
                    <Edit className="h-4 w-4 mr-2" />
                    Edit Vessel
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Edit Vessel Information</DialogTitle>
                  </DialogHeader>
                  <Form {...vesselForm}>
                    <form onSubmit={vesselForm.handleSubmit(onUpdateVessel)} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={vesselForm.control}
                          name="vesselName"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Vessel Name</FormLabel>
                              <FormControl>
                                <Input {...field} data-testid="input-vessel-name" />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={vesselForm.control}
                          name="vesselImoNumber"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>IMO Number</FormLabel>
                              <FormControl>
                                <Input {...field} placeholder="e.g., 9123456" data-testid="input-vessel-imo" />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      
                      <div className="grid grid-cols-3 gap-4">
                        <FormField
                          control={vesselForm.control}
                          name="vesselType"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Vessel Type</FormLabel>
                              <Select onValueChange={field.onChange} defaultValue={field.value}>
                                <FormControl>
                                  <SelectTrigger data-testid="select-vessel-type">
                                    <SelectValue placeholder="Select type" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {VESSEL_TYPES.map((type) => (
                                    <SelectItem key={type} value={type}>
                                      {type.replace('_', ' ').toUpperCase()}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={vesselForm.control}
                          name="vesselFlag"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Flag</FormLabel>
                              <FormControl>
                                <Input {...field} placeholder="e.g., Singapore" data-testid="input-vessel-flag" />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={vesselForm.control}
                          name="vesselDwt"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>DWT (tons)</FormLabel>
                              <FormControl>
                                <Input type="number" {...field} placeholder="Deadweight tonnage" data-testid="input-vessel-dwt" />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={vesselForm.control}
                          name="portOfLoading"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Port of Loading</FormLabel>
                              <FormControl>
                                <Input {...field} data-testid="input-port-loading" />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={vesselForm.control}
                          name="portOfDischarge"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Port of Discharge</FormLabel>
                              <FormControl>
                                <Input {...field} data-testid="input-port-discharge" />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={vesselForm.control}
                          name="vesselEta"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>ETA</FormLabel>
                              <FormControl>
                                <Input type="datetime-local" {...field} data-testid="input-vessel-eta" />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={vesselForm.control}
                          name="vesselEtd"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>ETD</FormLabel>
                              <FormControl>
                                <Input type="datetime-local" {...field} data-testid="input-vessel-etd" />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={vesselForm.control}
                          name="vesselStatus"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Vessel Status</FormLabel>
                              <Select onValueChange={field.onChange} defaultValue={field.value}>
                                <FormControl>
                                  <SelectTrigger data-testid="select-vessel-status">
                                    <SelectValue placeholder="Select status" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {VESSEL_STATUSES.map((status) => (
                                    <SelectItem key={status} value={status}>
                                      {status.replace('_', ' ').toUpperCase()}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={vesselForm.control}
                          name="progressStage"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Progress Stage</FormLabel>
                              <Select onValueChange={field.onChange} defaultValue={field.value}>
                                <FormControl>
                                  <SelectTrigger data-testid="select-progress-stage">
                                    <SelectValue placeholder="Select stage" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {PROGRESS_STAGES.map((stage) => (
                                    <SelectItem key={stage.key} value={stage.key}>
                                      {stage.label} ({stage.percentage}%)
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <FormField
                        control={vesselForm.control}
                        name="cargoDescription"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Cargo Description</FormLabel>
                            <FormControl>
                              <Textarea {...field} placeholder="Detailed cargo description" data-testid="input-cargo-description" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <div className="flex justify-end gap-2">
                        <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>
                          Cancel
                        </Button>
                        <Button 
                          type="submit" 
                          disabled={updateVesselMutation.isPending}
                          data-testid="button-save-vessel"
                        >
                          {updateVesselMutation.isPending ? 'Saving...' : 'Save Changes'}
                        </Button>
                      </div>
                    </form>
                  </Form>
                </DialogContent>
              </Dialog>
              {onClose && (
                <Button variant="outline" onClick={onClose}>
                  Close
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Progress Tracking */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-lg font-semibold">Order Progress</h3>
              <Badge 
                variant="outline" 
                className="text-sm"
                data-testid="progress-percentage"
              >
                {order.progressPercentage || currentStage.percentage}% Complete
              </Badge>
            </div>
            <Progress 
              value={order.progressPercentage || currentStage.percentage} 
              className="mb-2"
              data-testid="progress-bar"
            />
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>{currentStage.label}</span>
              <span>Stage {PROGRESS_STAGES.findIndex(s => s.key === order.progressStage) + 1} of {PROGRESS_STAGES.length}</span>
            </div>
          </div>

          {/* Key Information Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Vessel Details */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Ship className="h-4 w-4" />
                  Vessel Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div>
                  <span className="font-medium">Name:</span> {order.vesselName || '-'}
                </div>
                <div>
                  <span className="font-medium">Type:</span> {order.vesselType?.replace('_', ' ') || '-'}
                </div>
                <div>
                  <span className="font-medium">Flag:</span> {order.vesselFlag || '-'}
                </div>
                <div>
                  <span className="font-medium">DWT:</span> {order.vesselDwt ? `${order.vesselDwt.toLocaleString()} tons` : '-'}
                </div>
                <div>
                  <span className="font-medium">Status:</span>{' '}
                  <Badge variant="outline" className={getStatusColor(order.vesselStatus || 'unknown')}>
                    {order.vesselStatus?.replace('_', ' ') || 'Unknown'}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {/* Port Information */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Anchor className="h-4 w-4" />
                  Port Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div>
                  <span className="font-medium">Loading Port:</span> {order.portOfLoading || '-'}
                </div>
                <div>
                  <span className="font-medium">Discharge Port:</span> {order.portOfDischarge || '-'}
                </div>
                <div>
                  <span className="font-medium">Loading Berth:</span> {order.loadingBerth || '-'}
                </div>
                <div>
                  <span className="font-medium">Discharge Berth:</span> {order.dischargeBerth || '-'}
                </div>
              </CardContent>
            </Card>

            {/* Schedule Information */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Schedule
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div>
                  <span className="font-medium">ETA:</span> {formatDateTime(order.vesselEta)}
                </div>
                <div>
                  <span className="font-medium">ETD:</span> {formatDateTime(order.vesselEtd)}
                </div>
                <div>
                  <span className="font-medium">Loading Date:</span> {formatDateTime(order.loadingDate)}
                </div>
                <div>
                  <span className="font-medium">Discharge Date:</span> {formatDateTime(order.dischargingDate)}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Current Position */}
          {(order.currentLatitude && order.currentLongitude) && (
            <Card className="mt-4">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Navigation className="h-4 w-4" />
                  Current Position
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex justify-between items-center">
                  <div className="space-y-1 text-sm">
                    <div>
                      <span className="font-medium">Coordinates:</span> {formatCoordinate(order.currentLatitude, order.currentLongitude)}
                    </div>
                    <div>
                      <span className="font-medium">Last Update:</span> {formatDateTime(order.lastPositionUpdate)}
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowPositionDialog(true)}
                    data-testid="button-update-position"
                  >
                    <MapPin className="h-4 w-4 mr-2" />
                    Update Position
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Cargo Information */}
          {order.cargoDescription && (
            <Card className="mt-4">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Info className="h-4 w-4" />
                  Cargo Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm">{order.cargoDescription}</p>
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card>

      {/* Position Update Dialog */}
      <Dialog open={showPositionDialog} onOpenChange={setShowPositionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Vessel Position</DialogTitle>
          </DialogHeader>
          <form onSubmit={(e) => {
            e.preventDefault();
            const formData = new FormData(e.currentTarget);
            updatePositionMutation.mutate({
              latitude: parseFloat(formData.get('latitude') as string),
              longitude: parseFloat(formData.get('longitude') as string)
            });
          }}>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Latitude</label>
                <Input 
                  name="latitude"
                  type="number" 
                  step="any"
                  placeholder="e.g., 37.7749"
                  defaultValue={order.currentLatitude}
                  required
                  data-testid="input-latitude"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Longitude</label>
                <Input 
                  name="longitude"
                  type="number" 
                  step="any"
                  placeholder="e.g., -122.4194"
                  defaultValue={order.currentLongitude}
                  required
                  data-testid="input-longitude"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setShowPositionDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={updatePositionMutation.isPending}
                  data-testid="button-save-position"
                >
                  {updatePositionMutation.isPending ? 'Updating...' : 'Update Position'}
                </Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}